<script setup>
/*
========================================================================
StepReport_Modal_AddProduct.vue

Modal for adding additional products from the Report step.
Two-view flow:
1. Product Grid - User selects a product from the catalog
2. Product Details - User selects size/variant/lumen output

Uses ProductDetailsPanel for the details view, keeping the product
grid view in this component.
========================================================================
*/
import { ref, computed } from "vue";
import { useMainStore } from "@/stores/store.js";
import { Utils } from "@/utils.js";
import BaseModalPanel from "./utils/BaseModalPanel.vue";
import ProductDetailsPanel from "./utils/ProductDetailsPanel.vue";

const store = useMainStore();

/*
------------------------------------------------------------------------
Props
------------------------------------------------------------------------
*/
const props = defineProps({
	// Controls modal visibility
	show: {
		type: Boolean,
		required: true,
	},
	// Function to close the modal (provided by parent)
	fnCancelModal: {
		type: Function,
		required: true,
	},
});

/*
------------------------------------------------------------------------
Local State
------------------------------------------------------------------------
*/
// Currently selected product ID (empty = show grid, has value = show details)
const selectedProductId = ref("");
// Selected product data from catalog
const selectedProductData = ref(null);
// View state: false = product grid, true = product details
const showSizeSelection = ref(false);
// Acoustic-only filter checkbox state
const showAcousticOnlyFilter = ref(false);

/*
------------------------------------------------------------------------
Reset modal state to initial (product grid view)
Called when modal closes to ensure fresh state on next open.
------------------------------------------------------------------------
*/
const resetModalState = () => {
	selectedProductId.value = "";
	selectedProductData.value = null;
	showSizeSelection.value = false;
	showAcousticOnlyFilter.value = false;
};

/*
------------------------------------------------------------------------
Check if a product has acoustic-only options
A product has acoustic-only options if:
1. Any size/variant has lit: false, OR
2. Any extended data entry has acoustic_only_version: true
------------------------------------------------------------------------
*/
const productHasAcousticOnlyOption = (productId) => {
	const product = store.products.find((p) => p.id === productId);
	if (!product) return false;

	// Check sizes for lit: false
	if (product.sizes?.items) {
		for (const size of Object.values(product.sizes.items)) {
			if (size.lit === false) return true;
		}
	}

	// Check variants for lit: false
	if (product.sizes?.variants?.all) {
		for (const variant of Object.values(product.sizes.variants.all)) {
			if (variant.lit === false) return true;
		}
	}

	// Check extended data for acoustic_only_version: true
	const extendedData = store.productsExtended?.[productId];
	if (extendedData && Array.isArray(extendedData)) {
		for (const entry of extendedData) {
			if (entry.acoustic_only_version === true) return true;
		}
	}

	return false;
};

/*
------------------------------------------------------------------------
Computed: Filtered products based on acoustic-only filter
------------------------------------------------------------------------
*/
const filteredProducts = computed(() => {
	if (!showAcousticOnlyFilter.value) {
		return store.products;
	}
	return store.products.filter((product) =>
		productHasAcousticOnlyOption(product.id),
	);
});

/*
------------------------------------------------------------------------
Product Grid Handlers
------------------------------------------------------------------------
*/

// Handle product click in grid - show details view
const handleProductClick = (productId) => {
	$log.log("[StepReport_Modal_AddProduct:handleProductClick] Product clicked:", productId);

	// Load product data from catalog
	const productData = store.products.find((p) => p.id === productId);

	if (!productData) {
		$log.error("[StepReport_Modal_AddProduct:handleProductClick] Product not found:", productId);
		return;
	}

	selectedProductId.value = productId;
	selectedProductData.value = productData;
	showSizeSelection.value = true;
};

/*
------------------------------------------------------------------------
ProductDetailsPanel Event Handlers
------------------------------------------------------------------------
*/

// Handle confirm from ProductDetailsPanel - add product to store
const handleDetailsConfirm = (payload) => {
	$log.log("[StepReport_Modal_AddProduct:handleDetailsConfirm] Confirm received:", payload);

	// Add the complete product to store
	store.addProduct({
		product: payload.productId,
		shape: payload.shape,
		size: payload.size,
		size_variant: payload.size_variant,
		metric_sabins: payload.metric_sabins,
		data: payload.data,
		dataExtended: payload.dataExtended,
		selectedLumenOutput: payload.selectedLumenOutput,
		manualUnits: 1,
		calculation: null,
	});

	// Reset modal state so next open shows product grid
	resetModalState();

	// Close modal
	props.fnCancelModal();

	// Trigger calculation with new product
	store.calculateProductsBatch();
};

// Handle back from ProductDetailsPanel - return to product grid
const handleDetailsBack = () => {
	$log.log("[StepReport_Modal_AddProduct:handleDetailsBack] Back to product grid");
	selectedProductId.value = "";
	selectedProductData.value = null;
	showSizeSelection.value = false;
};

// Handle cancel from ProductDetailsPanel
const handleDetailsCancel = () => {
	// In details view, cancel goes back to grid
	handleDetailsBack();
};

/*
------------------------------------------------------------------------
Modal Handlers
------------------------------------------------------------------------
*/

// Handle modal cancel (backdrop click or close button)
const handleCancel = () => {
	// Reset modal state so next open shows product grid
	resetModalState();
	props.fnCancelModal();
};

// Handle escape key - context-aware behavior
const handleEscape = () => {
	if (showSizeSelection.value) {
		// In details view - go back to grid
		handleDetailsBack();
	} else {
		// In grid view - close modal
		handleCancel();
	}
};
</script>

<template>
	<!--
    ========================================================================
    Add Product Modal
    Wrapped in BaseModalPanel for consistent modal behavior (backdrop,
    escape handling, focus trap).
    ========================================================================
    -->
	<BaseModalPanel
		:show="show"
		:on-close="handleCancel"
		:on-escape="handleEscape"
		:show-backdrop="true"
		:show-close-button="!showSizeSelection"
		:topmost="true"
	>
		<!--
        ====================================================================
        View 1: Product Selection Grid
        Shown when user hasn't selected a product yet.
        ====================================================================
        -->
		<template v-if="!showSizeSelection">
			<!--
            ----------------------------------------------------------------
            Scrollable container for grid view
            ----------------------------------------------------------------
            -->
			<div class="flex h-full flex-col overflow-y-auto">
				<!--
                ----------------------------------------------------------------
                Header (sticky within scroll container)
                ----------------------------------------------------------------
                -->
				<div
					class="sticky top-0 z-10 flex flex-col justify-between border-b border-gray-200 bg-white px-8 py-6"
				>
					<h2 class="text-2xl font-bold text-black">Add Another Product</h2>
					<p class="text-gray-700">
						Select a product to add to your acoustic analysis:
					</p>

					<!-- Acoustic Only Filter Checkbox -->
					<label class="mt-3 flex cursor-pointer items-center gap-2">
						<input
							type="checkbox"
							v-model="showAcousticOnlyFilter"
							class="h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent"
						/>
						<span class="text-sm text-gray-700"
							>Limit to products with acoustic-only/non-lit options.</span
						>
					</label>
				</div>

				<!--
                ----------------------------------------------------------------
                Product Grid Content
                ----------------------------------------------------------------
                -->
				<div class="flex-1 px-8 py-6">
					<TransitionGroup
						tag="ul"
						name="product-list"
						role="list"
						class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4"
					>
						<li
							v-for="item in filteredProducts"
							:key="item.id"
							class="hover-effect col-span-1 flex cursor-pointer flex-col divide-y divide-gray-200 bg-white text-center"
							@click="handleProductClick(item.id)"
						>
							<a
								href="#"
								@click.prevent
								class="relative flex flex-1 flex-col overflow-hidden p-5 pt-[130px] text-center"
							>
								<img
									class="absolute left-1/2 top-0 mx-auto -mt-2 w-36 min-w-[150px] -translate-x-1/2"
									:src="Utils.resolveHeroImage(item.images.hero)"
									:alt="`Product image of: ${item.name}.`"
								/>
								<h3 class="mt-6 text-sm font-bold uppercase text-gray-900">
									{{ item.name }}
								</h3>
							</a>
						</li>
					</TransitionGroup>

					<!-- No results message when filter is active -->
					<p
						v-if="filteredProducts.length === 0 && showAcousticOnlyFilter"
						class="py-8 text-center text-gray-500"
					>
						No products with acoustic-only options found.
					</p>
				</div>
			</div>
		</template>

		<!--
        ====================================================================
        View 2: Product Details / Size Selection
        Shown after user clicks a product in the grid.
        Uses ProductDetailsPanel reusable component.
        ====================================================================
        -->
		<template v-if="showSizeSelection && selectedProductData">
			<ProductDetailsPanel
				:productData="selectedProductData"
				:productId="selectedProductId"
				:showFooterActions="true"
				:showBackButton="true"
				confirmButtonLabel="Add Product"
				@confirm="handleDetailsConfirm"
				@back="handleDetailsBack"
				@cancel="handleDetailsCancel"
			/>
		</template>
	</BaseModalPanel>
</template>

<style scoped>
/*
------------------------------------------------------------------------
Hover effect for product grid items
------------------------------------------------------------------------
*/
.hover-effect {
	text-align: center;
	transition: transform 1s ease;
	transform-origin: center;
}

.hover-effect:hover {
	transform: scale(1.05);
}

/*
------------------------------------------------------------------------
TransitionGroup animations for product filtering
Subtle fade + scale effect when products appear/disappear
------------------------------------------------------------------------
*/
.product-list-enter-active,
.product-list-leave-active {
	transition:
		opacity 0.25s ease,
		transform 0.25s ease;
}

.product-list-enter-from {
	opacity: 0;
	transform: scale(0.9);
}

.product-list-leave-to {
	opacity: 0;
	transform: scale(0.9);
}

/* Smooth repositioning of remaining items */
.product-list-move {
	transition: transform 0.3s ease;
}

/* Ensure leaving items don't affect layout during exit */
.product-list-leave-active {
	position: absolute;
}
</style>
